import {
  ConfirmModal,
  PROJECTS_DEFAULT_QUERY_PARAMS,
  PROJECTS_TABLE_COLUMNS,
  PageWrapper,
  ProjectsTable,
  Search,
} from "@/components";
import * as GlobalS from "@/components/styled";
import { CONTENT_WIDTH, PROJECT_STATUS, RIGHTS } from "@/enums";
import { routes } from "@/helpers";
import { useAllRights, useToggle } from "@/hooks";
import { useUserContext } from "@/providers";
import { calculateIsUserProjectLeader } from "@/providers/helpers";
import { useDeleteProjectMutation, useSearchProjectsQuery } from "@/services";
import { faCirclePlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Row, Switch } from "antd";
import { Fragment, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BooleanParam, StringParam, useQueryParams } from "use-query-params";

const ProjectList = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const rights = useAllRights();
  const { userToken, hasRight } = useUserContext();
  const isAdmin = hasRight([RIGHTS.writeAllProjects]);

  const [queryParams, setQueryParams] = useQueryParams({
    ...PROJECTS_DEFAULT_QUERY_PARAMS,
    description: StringParam,
    myProjects: BooleanParam,
    RequestorFullName: StringParam,
    ProjectLeaderFullName: StringParam,
  });

  const [projects, setProjects] = useState();
  const [pendingItemToDelete, setPendingItemToDelete] = useState();
  const [deleteConfirmationDialogHidden, toggleDeleteConfirmationDialogHidden] =
    useToggle(true);

  const {
    data: { data: searchResults, totalCount } = {},
    isLoading: loadingSearchResults,
    error: searchProjectsError,
  } = useSearchProjectsQuery(queryParams);
  const [deleteProject, { isLoading: isDeleting, error: isDeleteError }] =
    useDeleteProjectMutation();

  useEffect(() => {
    setProjects(searchResults);
  }, [searchResults]);

  useEffect(() => {
    if (!searchProjectsError) return;

    toast.error(intl.formatMessage({ id: "general.toastSomethingWentWrong" }));
  }, [intl, searchProjectsError]);

  useEffect(() => {
    if (isDeleteError)
      toast.error(
        intl.formatMessage({ id: "general.toastSomethingWentWrong" })
      );
  }, [isDeleteError, intl]);

  const handleModalToggle = (id) => {
    setPendingItemToDelete(id);
    toggleDeleteConfirmationDialogHidden();
  };

  const handleDelete = () => {
    deleteProject(pendingItemToDelete)
      .unwrap()
      .then(() => {
        setProjects(projects?.filter(({ id }) => id !== pendingItemToDelete));
        setPendingItemToDelete();
        toggleDeleteConfirmationDialogHidden();
        toast.success(intl.formatMessage({ id: "general.toastChangesSaved" }));
      });
  };

  const renderDeleteButton = (project) => {
    const { id, status, requestor } = project;

    if (status !== PROJECT_STATUS.intake.draft.id) return;

    const userId = userToken?.user?.id;
    if (!userId) return;

    // can delete if user is requestor, admin, or project leader
    const canDelete =
      requestor === userId ||
      isAdmin ||
      calculateIsUserProjectLeader(loadingSearchResults, project, userToken);

    if (!canDelete) return;

    return (
      <Button
        type="link"
        data-test-id={`project-list-delete-${id}`}
        onClick={() => handleModalToggle(id)}
        icon={<FontAwesomeIcon icon={faTrash} />}
      />
    );
  };

  return (
    <Fragment>
      <PageWrapper pageTitle={intl.formatMessage({ id: "projects.title" })}>
        <Row className="pb-2" align="middle">
          <Col
            xxl={CONTENT_WIDTH.oneThird}
            lg={CONTENT_WIDTH.half}
            md={CONTENT_WIDTH.twoThirds}
            xxs={CONTENT_WIDTH.max}
          >
            <Search
              queryParams={queryParams}
              setQueryParams={setQueryParams}
              loadingSearchResults={loadingSearchResults}
              clearAll={() => {
                setQueryParams({}, "push");
              }}
              searchTooltip={intl.formatMessage({
                id: "projects.searchTooltip",
              })}
              searchPlaceholder={intl.formatMessage({
                id: "general.searchPlaceholder",
              })}
              parentComponentName="project-list"
            />
          </Col>
          <Col className="pl-1">
            <Switch
              data-test-id="my-projects-switch"
              style={{ minWidth: "100px" }}
              checked={queryParams.myProjects}
              onChange={(value) =>
                setQueryParams({ ...queryParams, myProjects: value })
              }
              checkedChildren={intl.formatMessage({
                id: "general.myProjects",
              })}
              unCheckedChildren={intl.formatMessage({
                id: "general.allProjects",
              })}
            />
          </Col>
        </Row>
        <Row>
          <Col span={CONTENT_WIDTH.max}>
            <GlobalS.PageContentCard
              title="Projects"
              extra={
                <>
                  {rights.canCreateIntake && (
                    <Button
                      data-test-id="project-list-add-intake"
                      type="primary"
                      onClick={() => navigate(routes.projects.new)}
                      icon={<FontAwesomeIcon icon={faCirclePlus} />}
                    >
                      {intl.formatMessage({ id: "projectIntake.create" })}
                    </Button>
                  )}
                </>
              }
            >
              <ProjectsTable
                columnsToDisplay={[
                  PROJECTS_TABLE_COLUMNS.intake,
                  PROJECTS_TABLE_COLUMNS.projectNumber,
                  PROJECTS_TABLE_COLUMNS.status,
                  PROJECTS_TABLE_COLUMNS.projectName,
                  PROJECTS_TABLE_COLUMNS.projectLeaders,
                  PROJECTS_TABLE_COLUMNS.projectType,
                  PROJECTS_TABLE_COLUMNS.productCategory,
                  PROJECTS_TABLE_COLUMNS.productLine0,
                  PROJECTS_TABLE_COLUMNS.productLine1,
                  PROJECTS_TABLE_COLUMNS.productLine2,
                  PROJECTS_TABLE_COLUMNS.requestor,
                ]}
                customColumns={[
                  {
                    title: intl.formatMessage({ id: "general.actions" }),
                    render: (project) => renderDeleteButton(project),
                  },
                ]}
                projects={projects}
                loading={loadingSearchResults}
                scroll={{ x: true }}
                totalCount={totalCount}
                queryParams={queryParams}
                setQueryParams={setQueryParams}
              />
            </GlobalS.PageContentCard>
          </Col>
        </Row>
        <ConfirmModal
          loading={isDeleting}
          onSaveChanges={handleDelete}
          hidden={deleteConfirmationDialogHidden}
          toggle={toggleDeleteConfirmationDialogHidden}
          headerTitleKey={"projects.deleteIntakeModalTitle"}
          bodyTextKey={"projects.deleteIntakeModalDescription"}
          confirmationTimeoutSeconds={5}
        />
      </PageWrapper>
    </Fragment>
  );
};

export default ProjectList;
